import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import CashAccount from "../CashAccount";
import ModelProxyClass from "./ModelProxyClass";
import AmountDetails from "../AmountDetails";
import Person from "../Person";
import CashAccountMachineRecordDetails from "../CashAccountMachineRecordDetails";
import CashAccountBankDetails from "../CashAccountBankDetails";
import CashAccountVaultDetails from "../CashAccountVaultDetails";
import CashAccountOffageDetails from "../CashAccountOffageDetails";


/**
 * @class CashAccountBase
 * @extends ModelBaseClass
 * @property {number} id (int64)
 * @property {number} tenantId (int64)
 * @property {'Person'|'MachineRecord'|'Bank'|'Vault'|'Offage'} type
 * @property {string} name
 * @property {number} emergencyRamClearDropCount (integer)
 * @property {number} uncountedAmount
 * @property {AmountDetails} uncountedDetails
 * @property {number} countedAmount
 * @property {AmountDetails} countedDetails
 * @property {number} totalAmount
 * @property {boolean} activeFlag
 * @property {Date} dateCreated (date and time)
 * @property {Person} personDetails set if type is Person
 * @property {CashAccountMachineRecordDetails} machineRecordDetails set if type is MachineRecord
 * @property {CashAccountBankDetails} bankDetails set if type is Bank
 * @property {CashAccountVaultDetails} vaultDetails set if type is Vault
 * @property {CashAccountOffageDetails} offageDetails set if type is Offage
 */
class CashAccountBase extends ModelBaseClass {
	/**
	 * @type {number} id (int64)
	 */
	id;
	/**
	 * @type {number} tenantId (int64)
	 */
	tenantId;
	/**
	 * @type {'Person'|'MachineRecord'|'Bank'|'Vault'|'Offage'} type
	 */
	type;
	/**
	 * @type {string} name
	 */
	name;
	/**
	 * @type {number} emergencyRamClearDropCount (integer)
	 */
	emergencyRamClearDropCount;
	/**
	 * @type {number} uncountedAmount
	 */
	uncountedAmount;
	/**
	 * @type {AmountDetails} uncountedDetails
	 */
	uncountedDetails;
	/**
	 * @type {number} countedAmount
	 */
	countedAmount;
	/**
	 * @type {AmountDetails} countedDetails
	 */
	countedDetails;
	/**
	 * @type {number} totalAmount
	 */
	totalAmount;
	/**
	 * @type {boolean} activeFlag
	 */
	activeFlag;
	/**
	 * @type {Date} dateCreated (date and time)
	 */
	dateCreated;
	/**
	 * set if type is Person
	 * @type {Person} personDetails
	 */
	personDetails;
	/**
	 * set if type is MachineRecord
	 * @type {CashAccountMachineRecordDetails} machineRecordDetails
	 */
	machineRecordDetails;
	/**
	 * set if type is Bank
	 * @type {CashAccountBankDetails} bankDetails
	 */
	bankDetails;
	/**
	 * set if type is Vault
	 * @type {CashAccountVaultDetails} vaultDetails
	 */
	vaultDetails;
	/**
	 * set if type is Offage
	 * @type {CashAccountOffageDetails} offageDetails
	 */
	offageDetails;

	/**
	 * Instantiates a new instance of CashAccount based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {CashAccount}
	 */
	static create(genericObject) {
		const newCashAccount = new CashAccount();
		newCashAccount.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newCashAccount;
	}

	/**
	 * Instantiates a new array of CashAccount based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[CashAccount]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newCashAccountArray = [];
		genericArray.forEach(genericObject => {
			newCashAccountArray.push(CashAccount.create(genericObject));
		});
		return newCashAccountArray;
	}
}

const _modelDefinition = [
	ModelBaseClass.createModelProperty('id', 'integer'),
	ModelBaseClass.createModelProperty('tenantId', 'integer'),
	ModelBaseClass.createModelProperty('type', 'string'),
	ModelBaseClass.createModelProperty('name', 'string'),
	ModelBaseClass.createModelProperty('emergencyRamClearDropCount', 'integer'),
	ModelBaseClass.createModelProperty('uncountedAmount', 'float'),
	ModelBaseClass.createModelProperty('uncountedDetails', 'AmountDetails'),
	ModelBaseClass.createModelProperty('countedAmount', 'float'),
	ModelBaseClass.createModelProperty('countedDetails', 'AmountDetails'),
	ModelBaseClass.createModelProperty('totalAmount', 'float'),
	ModelBaseClass.createModelProperty('activeFlag', 'boolean'),
	ModelBaseClass.createModelProperty('dateCreated', 'datetime'),
	ModelBaseClass.createModelProperty('personDetails', 'Person'),
	ModelBaseClass.createModelProperty('machineRecordDetails', 'CashAccountMachineRecordDetails'),
	ModelBaseClass.createModelProperty('bankDetails', 'CashAccountBankDetails'),
	ModelBaseClass.createModelProperty('vaultDetails', 'CashAccountVaultDetails'),
	ModelBaseClass.createModelProperty('offageDetails', 'CashAccountOffageDetails'),
];

export default CashAccountBase;
